import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal
} from '@angular/core';
import {MaterialModule} from '../../../material.module';
import {
  ConnectRun,
  ConnectRunStep,
  ConnectWorkflowCanvasBackground,
  ConnectWorkflowCanvasBackgroundStyle,
  ConnectWorkflowEdge,
  ConnectWorkflowNode,
  ConnectWorkflowsService,
} from '../../../services/connect/connect-workflows.service';
import {
  ConnectConnectionsService,
  ConnectServerConnection
} from '../../../services/connect/connect-connections.service';
import {EnvironmentService} from '../../../services/environment.service';
import {ToastService} from '../../../services/layout/toast.service';
import {of, Subscription, timeout} from 'rxjs';
import {catchError, finalize, take} from 'rxjs/operators';

type InspectorTab = 'settings' | 'current' | 'previous';
type OnErrorPolicy = 'fail' | 'continue' | 'retry';

const INSPECTOR_WIDTH_STORAGE_KEY = 'workflow_inspector_width_px';
const INSPECTOR_WIDTH_DEFAULT = 360;
const INSPECTOR_WIDTH_MIN = 280;
const INSPECTOR_WIDTH_MAX = 560;

type NodePatchEvent = {nodeId: string; patch: Partial<ConnectWorkflowNode>};
type EdgePatchEvent = {edgeId: string; patch: Partial<ConnectWorkflowEdge>};
type BackgroundPatchEvent = { backgroundId: string; patch: Partial<ConnectWorkflowCanvasBackground> };
type BackgroundStylePatchEvent = { backgroundId: string; patch: Partial<ConnectWorkflowCanvasBackgroundStyle> };
type BackgroundDeletedEvent = { backgroundId: string };

@Component({
  selector: 'app-connect-workflow-inspector',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './connect-workflow-inspector.component.html',
  styleUrls: ['../connect-shared.scss', './connect-workflow-inspector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectWorkflowInspectorComponent implements OnDestroy {
  private readonly workflowsService = inject(ConnectWorkflowsService);
  private readonly connectionsService = inject(ConnectConnectionsService);
  private readonly env = inject(EnvironmentService);
  private readonly toastService = inject(ToastService);
  private readonly sub = new Subscription();

  workflowId = input<string | null>(null);
  visualizeRunId = input<string | null>(null);
  selectedNode = input<ConnectWorkflowNode | null>(null);
  selectedEdge = input<ConnectWorkflowEdge | null>(null);
    selectedBackground = input<ConnectWorkflowCanvasBackground | null>(null);

  readonly nodePatched = output<NodePatchEvent>();
  readonly edgePatched = output<EdgePatchEvent>();
    readonly backgroundPatched = output<BackgroundPatchEvent>();
    readonly backgroundStylePatched = output<BackgroundStylePatchEvent>();
    readonly backgroundDeleted = output<BackgroundDeletedEvent>();

  readonly isFullscreen = signal<boolean>(false);
  readonly activeTab = signal<InspectorTab>('settings');
  readonly isResizing = signal<boolean>(false);
  readonly widthPx = signal<number>(this.readPersistedWidth());

  readonly runs = signal<ConnectRun[] | null>(null);
  readonly runsLoading = signal<boolean>(false);
  readonly runsError = signal<string | null>(null);
  readonly selectedRunId = signal<string>('');
  readonly selectedRunInput = signal<unknown | undefined>(undefined);
  readonly selectedRunOutput = signal<unknown | undefined>(undefined);
  readonly selectedRunStatus = signal<string | undefined>(undefined);

  readonly inputJsonDraft = signal<string>('');
  readonly inputJsonError = signal<string | null>(null);
  readonly httpHeadersDraft = signal<string>('');
  readonly httpHeadersError = signal<string | null>(null);
  readonly httpBodyDraft = signal<string>('');
  readonly httpBodyError = signal<string | null>(null);

  readonly connections = signal<ConnectServerConnection[]>([]);
  readonly connectionsLoading = signal<boolean>(false);
  readonly connectionsError = signal<string | null>(null);

  private resizeStart: {x: number; width: number} | null = null;
  private readonly onWindowMouseMove = (event: MouseEvent) => this.onResizeMove(event);
  private readonly onWindowMouseUp = () => this.stopResize();

  private runsLoadSub: Subscription | null = null;
  private runDetailSub: Subscription | null = null;
  private lastRunsWorkflowId: string | null = null;
  private lastRunDetailKey: string | null = null;
  private lastDraftNodeId: string | null = null;
  private lastAppliedVisualizeRunId: string | null = null;

    readonly selectedKind = computed<'node' | 'edge' | 'background' | 'none'>(() => {
    if (this.selectedNode()) return 'node';
    if (this.selectedEdge()) return 'edge';
        if (this.selectedBackground()) return 'background';
    return 'none';
  });

  readonly selectedNodeType = computed(() => String(this.selectedNode()?.type ?? '').trim());
  readonly showRunsPanel = computed(() => this.selectedKind() === 'node' && !!this.workflowId() && (this.isFullscreen() || this.activeTab() === 'current'));

  readonly isWebhookTriggerNode = computed(() => {
    const t = String(this.selectedNodeType() || '').trim().toLowerCase();
    return t === 'trigger.webhook' || t === 'webhook_trigger';
  });

  readonly webhookPath = computed(() => {
    if (!this.isWebhookTriggerNode()) return '';
    const wfId = String(this.workflowId() || '').trim();
    const nodeId = String(this.selectedNode()?.id || '').trim();
    if (!wfId || !nodeId) return '';
    return `/api/v1/webhooks/${wfId}/${nodeId}`;
  });

  readonly webhookUrl = computed(() => {
    const path = this.webhookPath();
    if (!path) return '';

    const base = String(this.env.connectBaseServerUrl || '').replace(/\/+$/, '');
    if (!base) return this.toAbsoluteUrl(path);
    if (base.startsWith('/')) return this.toAbsoluteUrl(`${base}${path}`);
    return `${base}${path}`;
  });

  readonly webhookCurlSnippet = computed(() => {
    const url = this.webhookUrl();
    if (!url) return '';
    return [
      `curl -X POST '${url}?source=example' \\`,
      `  -H 'Content-Type: application/json' \\`,
      `  -H 'X-Idempotency-Key: 123' \\`,
      `  -d '{"event":"example","ok":true}'`,
    ].join('\n');
  });

  @HostBinding('class.is-fullscreen')
  get fullscreenClass(): boolean {
    return this.isFullscreen();
  }

  @HostBinding('style.--connect-inspector-width')
  get widthStyle(): string | null {
    if (this.isFullscreen()) return null;
    return `${this.widthPx()}px`;
  }

  @HostBinding('attr.data-fullscreen')
  get dataFullscreen(): string {
    return this.isFullscreen() ? 'true' : 'false';
  }

  constructor() {
    effect(() => {
      const width = this.widthPx();
      if (this.isFullscreen()) return;
      try {
        window.localStorage?.setItem(INSPECTOR_WIDTH_STORAGE_KEY, String(Math.round(width)));
      } catch {
        // ignore
      }
    });

    effect(() => {
      const shouldLoad = this.showRunsPanel();
      const wfId = this.workflowId();
      if (!shouldLoad || !wfId) return;
      if (this.runs() && this.lastRunsWorkflowId === wfId) return;
      this.loadRuns(wfId);
    });

    effect(() => {
      const shouldLoad = this.showRunsPanel();
      const wfId = this.workflowId();
      const nodeId = this.selectedNode()?.id;
      const arr = this.runs();
      if (!shouldLoad || !wfId || !nodeId) return;
      if (this.visualizeRunId()) return;
      if (this.selectedRunId()) return;
      if (!arr?.length) return;

      const latest = [...arr].sort((a, b) => {
        const ta = Date.parse(a.created_at || '') || 0;
        const tb = Date.parse(b.created_at || '') || 0;
        if (tb !== ta) return tb - ta;
        return String(b.id || '').localeCompare(String(a.id || ''));
      })[0];
      if (latest?.id) {
        this.selectedRunId.set(latest.id);
      }
    });

    effect(() => {
      const vis = typeof this.visualizeRunId() === 'string' ? this.visualizeRunId()!.trim() : '';
      if (!vis) {
        this.lastAppliedVisualizeRunId = null;
        return;
      }

      if (this.selectedRunId() !== vis) {
        this.selectedRunId.set(vis);
      }
      this.activeTab.set('current');
      this.lastAppliedVisualizeRunId = vis;
    });

    effect(() => {
      const shouldLoad = this.showRunsPanel();
      const wfId = this.workflowId();
      const nodeId = this.selectedNode()?.id;
      const runId = this.selectedRunId();
      if (!shouldLoad || !wfId || !nodeId || !runId) {
        this.selectedRunInput.set(undefined);
        this.selectedRunOutput.set(undefined);
        this.selectedRunStatus.set(undefined);
        this.lastRunDetailKey = null;
        this.runDetailSub?.unsubscribe();
        this.runDetailSub = null;
        return;
      }

      const key = `${runId}|${nodeId}`;
      if (this.lastRunDetailKey === key) return;
      this.lastRunDetailKey = key;
      this.loadRunDetail(runId, nodeId);
    });

    effect(() => {
      const kind = this.selectedKind();
      if (kind === 'none') return;
        if (kind === 'edge' || kind === 'background') {
        this.activeTab.set('settings');
      }
    });

    effect(() => {
      const node = this.selectedNode();
      const nodeId = node?.id ?? null;
      if (nodeId === this.lastDraftNodeId) return;
      this.lastDraftNodeId = nodeId;
      this.inputJsonError.set(null);
      this.httpHeadersError.set(null);
      this.httpBodyError.set(null);

      if (!node) {
        this.inputJsonDraft.set('');
        this.httpHeadersDraft.set('');
        this.httpBodyDraft.set('');
        return;
      }

      this.inputJsonDraft.set(this.formatJson(node['input'] ?? {}));
      const input = this.asRecord(node['input']);
      this.httpHeadersDraft.set(this.formatJson(input['headers'] ?? {}));

      const body = input['body'];
      if (typeof body === 'string') {
        this.httpBodyDraft.set(body);
      } else if (typeof body === 'undefined') {
        this.httpBodyDraft.set('');
      } else {
        this.httpBodyDraft.set(this.formatJson(body));
      }
    });

    this.loadConnections();
  }

  ngOnDestroy(): void {
    this.stopResize();
    this.sub.unsubscribe();
    this.runsLoadSub?.unsubscribe();
    this.runDetailSub?.unsubscribe();
  }

  toggleFullscreen(): void {
    this.isFullscreen.set(!this.isFullscreen());
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    if (!this.isFullscreen()) return;
    if (event.key === 'Escape') {
      this.isFullscreen.set(false);
    }
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.resizeStart = {x: event.clientX, width: this.widthPx()};
    this.isResizing.set(true);
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('mouseup', this.onWindowMouseUp, {once: true});
  }

  stopResize(): void {
    this.isResizing.set(false);
    this.resizeStart = null;
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
  }

  setActiveTab(tab: InspectorTab): void {
    this.activeTab.set(tab);
  }

  setSelectedRunId(runId: string): void {
    this.selectedRunId.set(runId || '');
  }

  patchNode(patch: Partial<ConnectWorkflowNode>): void {
    const node = this.selectedNode();
    if (!node) return;
    this.nodePatched.emit({nodeId: node.id, patch});
  }

  patchEdge(patch: Partial<ConnectWorkflowEdge>): void {
    const edge = this.selectedEdge();
    if (!edge) return;
    const edgeId = typeof edge.id === 'string' && edge.id ? edge.id : this.fallbackEdgeId(edge);
    this.edgePatched.emit({edgeId, patch});
  }

    patchBackground(patch: Partial<ConnectWorkflowCanvasBackground>): void {
        const bg = this.selectedBackground();
        if (!bg) return;
        this.backgroundPatched.emit({backgroundId: bg.id, patch});
    }

    patchBackgroundStyle(patch: Partial<ConnectWorkflowCanvasBackgroundStyle>): void {
        const bg = this.selectedBackground();
        if (!bg) return;
        this.backgroundStylePatched.emit({backgroundId: bg.id, patch});
    }

    deleteBackground(): void {
        const bg = this.selectedBackground();
        if (!bg) return;
        this.backgroundDeleted.emit({backgroundId: bg.id});
    }

  updateNodeLabel(value: string): void {
    this.patchNode({label: value});
  }

    updateBackgroundLabel(value: string): void {
        const next = String(value ?? '').trim();
        this.patchBackground({label: next || undefined});
    }

    updateBackgroundColor(value: string): void {
        const next = String(value ?? '').trim();
        this.patchBackgroundStyle({fill: next || undefined});
    }

    updateBackgroundOpacity(value: string): void {
        const next = value === '' ? undefined : Number(value);
        if (next === undefined) {
            this.patchBackgroundStyle({opacity: undefined});
            return;
        }
        if (!isFinite(next)) return;
        this.patchBackgroundStyle({opacity: Math.min(Math.max(next, 0), 1)});
    }

    updateBackgroundRadius(value: string): void {
        const next = value === '' ? undefined : Number(value);
        if (next === undefined) {
            this.patchBackgroundStyle({radius: undefined});
            return;
        }
        if (!isFinite(next)) return;
        this.patchBackgroundStyle({radius: Math.max(0, next)});
    }

    updateBackgroundWidth(value: string): void {
        const bg = this.selectedBackground();
        if (!bg) return;
        const next = value === '' ? undefined : Number(value);
        if (next === undefined) return;
        if (!isFinite(next)) return;
        const width = Math.max(1, next);
        this.patchBackground({size: {...bg.size, width}});
    }

    updateBackgroundHeight(value: string): void {
        const bg = this.selectedBackground();
        if (!bg) return;
        const next = value === '' ? undefined : Number(value);
        if (next === undefined) return;
        if (!isFinite(next)) return;
        const height = Math.max(1, next);
        this.patchBackground({size: {...bg.size, height}});
    }

    updateBackgroundLocked(locked: boolean): void {
        this.patchBackground({locked: !!locked});
    }

  updateNodeRef(value: string): void {
    const next = value.trim();
    if (!next) return;
    this.patchNode({ref: next});
  }

  updateNodeOnError(value: string): void {
    const policy = (value || '').trim() as OnErrorPolicy;
    if (!policy) {
      this.patchNode({on_error: undefined});
      return;
    }
    this.patchNode({on_error: policy});
  }

  updateNodeRetries(value: string): void {
    const next = value === '' ? undefined : Number(value);
    if (next === undefined) {
      this.patchNode({retries: undefined});
      return;
    }
    if (!Number.isFinite(next) || next < 0) return;
    this.patchNode({retries: Math.floor(next)});
  }

  updateNodeTimeout(value: string): void {
    const next = value === '' ? undefined : Number(value);
    if (next === undefined) {
      this.patchNode({timeout_ms: undefined});
      return;
    }
    if (!Number.isFinite(next) || next < 1) return;
    this.patchNode({timeout_ms: Math.floor(next)});
  }

  updateNodeIdempotencyKey(value: string): void {
    const next = value.trim();
    this.patchNode({idempotency_key: next ? next : undefined});
  }

  updateHttpMethod(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const input = this.asRecord(node['input']);
    this.patchNode({input: {...input, method: value}});
  }

  updateHttpUrl(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const input = this.asRecord(node['input']);
    this.patchNode({input: {...input, url: value}});
  }

  updateConnectorConnection(value: string): void {
    const next = value.trim();
    this.patchNode({connection: next ? next : undefined});
  }

  updateConnectorObjectType(value: string): void {
    this.patchNode({objectType: value.trim() ? value.trim() : undefined});
  }

  updateConditionExpression(value: string): void {
    this.patchNode({condition: {expression: value}});
  }

  updateLoopCollection(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const loop = this.asRecord(node['loop']);
    this.patchNode({loop: {...loop, collection: value}});
  }

  updateLoopItemAlias(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const loop = this.asRecord(node['loop']);
    this.patchNode({loop: {...loop, item_alias: value}});
  }

  updateLoopConcurrency(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const next = value === '' ? undefined : Number(value);
    if (next !== undefined && (!Number.isFinite(next) || next < 1)) return;
    const loop = this.asRecord(node['loop']);
    this.patchNode({loop: {...loop, concurrency: next === undefined ? undefined : Math.floor(next)}});
  }

    updateLoopMaxItems(value: string): void {
        const node = this.selectedNode();
        if (!node) return;
        const next = value === '' ? undefined : Number(value);
        if (next !== undefined && (!Number.isFinite(next) || next < 0)) return;
        const loop = this.asRecord(node['loop']);
        this.patchNode({loop: {...loop, max_items: next === undefined ? undefined : Math.floor(next)}});
    }

    updateLoopUntil(value: string): void {
        const node = this.selectedNode();
        if (!node) return;
        const loop = this.asRecord(node['loop']);
        const next = value.trim();
        this.patchNode({loop: {...loop, until: next ? next : undefined}});
    }

  updateWaitDelayMs(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const next = value === '' ? undefined : Number(value);
    if (next !== undefined && (!Number.isFinite(next) || next < 0)) return;
    const wait = this.asRecord(node['wait']);
    this.patchNode({wait: {...wait, delay_ms: next === undefined ? undefined : Math.floor(next)}});
  }

    updateWaitUntil(value: string): void {
        const node = this.selectedNode();
        if (!node) return;
        const wait = this.asRecord(node['wait']);
        const next = value.trim();
        this.patchNode({wait: {...wait, until: next ? next : undefined}});
    }

  updateTransformLanguage(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const transform = this.asRecord(node['transform']);
    this.patchNode({transform: {...transform, language: value}});
  }

  updateTransformExpression(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const transform = this.asRecord(node['transform']);
    this.patchNode({transform: {...transform, expression: value}});
  }

  updateJoinStrategy(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const join = this.asRecord(node['join']);
    this.patchNode({join: {...join, strategy: value}});
  }

  updateAiInstructions(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const xAi = this.asRecord(node['x-ai']);
    this.patchNode({['x-ai']: {...xAi, instructions: value}});
  }

  updateAiModel(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const xAi = this.asRecord(node['x-ai']);
    this.patchNode({['x-ai']: {...xAi, model: value.trim() ? value.trim() : undefined}});
  }

  updateAiMaxTokens(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const next = value === '' ? undefined : Number(value);
    if (next !== undefined && (!Number.isFinite(next) || next < 1)) return;
    const xAi = this.asRecord(node['x-ai']);
    this.patchNode({['x-ai']: {...xAi, max_tokens: next === undefined ? undefined : Math.floor(next)}});
  }

  updateAiTemperature(value: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const next = value === '' ? undefined : Number(value);
    if (next !== undefined && (!Number.isFinite(next) || next < 0 || next > 2)) return;
    const xAi = this.asRecord(node['x-ai']);
    this.patchNode({['x-ai']: {...xAi, temperature: next}});
  }

  updateEdgeLabel(value: string): void {
    this.patchEdge({label: value});
  }

  updateEdgeCondition(value: string): void {
    this.patchEdge({condition: value.trim() ? value : undefined});
  }

  updateEdgePriority(value: string): void {
    const next = value === '' ? undefined : Number(value);
    if (next === undefined) {
      this.patchEdge({priority: undefined});
      return;
    }
    if (!Number.isFinite(next) || next < 0) return;
    this.patchEdge({priority: Math.floor(next)});
  }

  updateEdgeOnError(value: string): void {
    const policy = (value || '').trim() as OnErrorPolicy;
    if (!policy) {
      this.patchEdge({on_error: undefined});
      return;
    }
    this.patchEdge({on_error: policy});
  }

  copyToClipboard(value: string, successMessage?: string): void {
    const text = String(value || '').trim();
    if (!text) return;

    const message = String(successMessage || 'Copied to clipboard').trim() || 'Copied to clipboard';
    const notify = () => this.toastService.showSuccess(message, 2500);

    const fallback = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        // ignore
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(notify)
        .catch(() => {
          fallback();
          notify();
        });
    } else {
      fallback();
      notify();
    }
  }

  async simulateSelectedNode(): Promise<void> {
    const wfId = this.workflowId();
    const node = this.selectedNode();
    if (!wfId || !node) return;
    if (String(node.type) !== 'control.loop') return;
    if (typeof this.selectedRunInput() === 'undefined') return;

    this.selectedRunOutput.set({status: 'running'});
    this.sub.add(
      this.workflowsService
        .simulateNode(wfId, node.id, this.selectedRunInput())
        .pipe(
          take(1),
          catchError(err => {
            const message = (err as {message?: string} | null)?.message || 'Simulate failed.';
            return of({output: {error: message}});
          }),
        )
        .subscribe(res => {
          this.selectedRunOutput.set(res?.output);
        }),
    );
  }

  formatJson(value: unknown): string {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value ?? null, null, 2);
    } catch {
      return String(value);
    }
  }

  humanConnectionOptions(): {value: string; label: string}[] {
    return this.connections()
      .map(c => ({value: c.name, label: c.name}))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private readPersistedWidth(): number {
    try {
      const raw = window.localStorage?.getItem(INSPECTOR_WIDTH_STORAGE_KEY);
      const parsed = raw ? Number(raw) : INSPECTOR_WIDTH_DEFAULT;
      if (!Number.isFinite(parsed)) return INSPECTOR_WIDTH_DEFAULT;
      return Math.min(INSPECTOR_WIDTH_MAX, Math.max(INSPECTOR_WIDTH_MIN, parsed));
    } catch {
      return INSPECTOR_WIDTH_DEFAULT;
    }
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this.resizeStart) return;
    const delta = event.clientX - this.resizeStart.x;
    const next = Math.min(INSPECTOR_WIDTH_MAX, Math.max(INSPECTOR_WIDTH_MIN, this.resizeStart.width - delta));
    this.widthPx.set(next);
  }

  private loadRuns(workflowId: string): void {
    this.runsLoadSub?.unsubscribe();
    this.runsLoadSub = null;
    this.lastRunsWorkflowId = workflowId;
    this.runsError.set(null);
    this.runsLoading.set(true);
    this.runs.set(null);
    this.selectedRunId.set('');

    this.runsLoadSub = this.workflowsService
      .listRuns(workflowId)
      .pipe(
        take(1),
        timeout({first: 20_000}),
        catchError(() => {
          console.error('Failed to load runs.');
          this.runsError.set('Could not load runs.');
          return of<ConnectRun[]>([]);
        }),
        finalize(() => this.runsLoading.set(false)),
      )
      .subscribe(items => {
        const list = Array.isArray(items) ? [...items] : [];
        list.sort((a, b) => {
          const ta = Date.parse(a.created_at || '') || 0;
          const tb = Date.parse(b.created_at || '') || 0;
          if (tb !== ta) return tb - ta;
          return String(b.id || '').localeCompare(String(a.id || ''));
        });
        this.runs.set(list);
      });
  }

  private loadRunDetail(runId: string, nodeId: string): void {
    this.runDetailSub?.unsubscribe();
    this.runDetailSub = null;
    this.selectedRunInput.set(undefined);
    this.selectedRunOutput.set(undefined);
    this.selectedRunStatus.set(undefined);

    this.runDetailSub = this.workflowsService
      .getRun(runId)
      .pipe(
        take(1),
        timeout({first: 20_000}),
        catchError(() => {
          console.error('Failed to load run detail.');
          return of<ConnectRun | null>(null);
        }),
      )
      .subscribe(detail => {
        const steps = Array.isArray(detail?.steps) ? detail!.steps : [];
        const step = steps.find(s => String(s.node_id) === String(nodeId)) as (ConnectRunStep & {context_input?: unknown}) | undefined;
        const input = step?.context_input ?? step?.input;
        this.selectedRunInput.set(input);
        this.selectedRunOutput.set(step?.output);
        this.selectedRunStatus.set(step?.status);
      });
  }

  private loadConnections(): void {
    this.connectionsLoading.set(true);
    this.connectionsError.set(null);
    this.sub.add(
      this.connectionsService
        .list()
        .pipe(
          take(1),
          catchError(() => {
            console.error('Failed to load connections.');
            this.connectionsError.set('Could not load connections.');
            return of<ConnectServerConnection[]>([]);
          }),
          finalize(() => this.connectionsLoading.set(false)),
        )
        .subscribe(items => {
          this.connections.set(Array.isArray(items) ? items : []);
        }),
    );
  }

  private fallbackEdgeId(edge: ConnectWorkflowEdge): string {
    const sourcePort = edge.source_handle || 'out';
    const targetPort = edge.target_handle || 'in';
    return `${edge.source}:${sourcePort}->${edge.target}:${targetPort}`;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return value as Record<string, unknown>;
  }

  setInputJsonDraft(value: string): void {
    this.inputJsonDraft.set(value);
  }

  applyInputJsonDraft(): void {
    const node = this.selectedNode();
    if (!node) return;
    const raw = this.inputJsonDraft().trim();
    if (!raw) {
      this.inputJsonError.set(null);
      this.patchNode({input: {}});
      return;
    }
    const parsed = this.safeParseJson(raw);
    if (!parsed.ok) {
      this.inputJsonError.set(parsed.error);
      return;
    }
    if (!parsed.value || typeof parsed.value !== 'object' || Array.isArray(parsed.value)) {
      this.inputJsonError.set('Input must be a JSON object.');
      return;
    }
    this.inputJsonError.set(null);
    this.patchNode({input: parsed.value as Record<string, unknown>});
  }

  setHttpHeadersDraft(value: string): void {
    this.httpHeadersDraft.set(value);
  }

  applyHttpHeadersDraft(): void {
    const node = this.selectedNode();
    if (!node) return;
    const raw = this.httpHeadersDraft().trim();
    const input = this.asRecord(node['input']);
    if (!raw) {
      this.httpHeadersError.set(null);
      this.patchNode({input: {...input, headers: undefined}});
      return;
    }
    const parsed = this.safeParseJson(raw);
    if (!parsed.ok) {
      this.httpHeadersError.set(parsed.error);
      return;
    }
    if (!parsed.value || typeof parsed.value !== 'object' || Array.isArray(parsed.value)) {
      this.httpHeadersError.set('Headers must be a JSON object.');
      return;
    }
    this.httpHeadersError.set(null);
    this.patchNode({input: {...input, headers: parsed.value as Record<string, unknown>}});
  }

  setHttpBodyDraft(value: string): void {
    this.httpBodyDraft.set(value);
  }

  applyHttpBodyDraft(): void {
    const node = this.selectedNode();
    if (!node) return;
    const raw = this.httpBodyDraft();
    const trimmed = raw.trim();
    const input = this.asRecord(node['input']);
    if (!trimmed) {
      this.httpBodyError.set(null);
      this.patchNode({input: {...input, body: undefined}});
      return;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = this.safeParseJson(trimmed);
      if (!parsed.ok) {
        this.httpBodyError.set(parsed.error);
        return;
      }
      this.httpBodyError.set(null);
      this.patchNode({input: {...input, body: parsed.value}});
      return;
    }
    this.httpBodyError.set(null);
    this.patchNode({input: {...input, body: raw}});
  }

  private safeParseJson(raw: string): {ok: true; value: unknown} | {ok: false; error: string} {
    try {
      return {ok: true, value: JSON.parse(raw)};
    } catch (err) {
      const message = (err as {message?: string} | null)?.message || 'Invalid JSON.';
      return {ok: false, error: message};
    }
  }

  private toAbsoluteUrl(path: string): string {
    const clean = path.startsWith('/') ? path : `/${path}`;
    try {
      return new URL(clean, window.location.origin).toString();
    } catch {
      return clean;
    }
  }
}
